import {
    getServerStoreData,
    setServerStoreData
} from '../../../services/proto/ServerStorage/service';
import {formsStore} from '../../../stores/Form';
import {common} from '@priority-software/priority-proto-api/lib';

/**
 * Actions: Form System Flags Management
 *
 * These actions provide an interface between the client-side form system flags
 * (stored in formsStore) and the server-side persistent storage (accessed via the ServerStorage service).
 * It defines a unified way to fetch, update, and read feature flags related to the form system
 * configuration such as field search behavior (Ignore case, Search From Start, etc.).
 */

const keyPrefix = 'formSystem';

/** List of all available keys **/
const keyMap = {
    fieldSearchIgnoreCase: `${keyPrefix}:fieldSearch:ignoreCase`,
    fieldSearchFromStart: `${keyPrefix}:fieldSearch:searchStart`
} as const;

export type TFormSystemFlags = {
    [K in keyof typeof keyMap]?: common.IConstant;
};

export const getFormSystemFlags = async () => {
    if (Object.keys(formsStore.formSystemFlags).length === 0) {
        const serverData = await getServerStoreData([`${keyPrefix}:*`], true, true);

        formsStore.formSystemFlags = Object.entries(keyMap).reduce(
            (acc, [clientKey, serverKey]) => {
                const serverValue = serverData[serverKey];
                return {
                    ...acc,
                    [clientKey]: serverValue || new common.Constant({Boolean: false})
                };
            },
            {} as const
        );
    }
};

export const setFormSystemFlags = async (data: Partial<TFormSystemFlags>) => {
    formsStore.formSystemFlags = {
        ...formsStore.formSystemFlags,
        ...data
    };

    const mappedData = Object.keys(data).reduce((acc, curr) => {
        const serverKey = keyMap[curr];

        return serverKey ? {...acc, [serverKey]: data[curr]} : acc;
    }, {});

    await setServerStoreData(mappedData, true, true);
};

export const getFormSystemBooleanFlag = (key: keyof typeof keyMap) => {
    return formsStore.formSystemFlags[key]?.Boolean ?? false;
};
